import { useState } from 'react';
import type { JSX } from 'react';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { useI18n } from '../i18n';
import type { StringKey } from '../i18n';
import { ApiError, UNAUTHORIZED, createApiClient } from '../lib/api';
import type { NetStatus } from '../lib/networkStatus';
import { AlertBanner } from './ui/AlertBanner';
import { AppBackground } from './ui/AppBackground';
import { DeviceInfoCard } from './ui/DeviceInfoCard';
import { GlobeHero } from './ui/GlobeHero';
import { Icon } from './ui/Icon';
import { NeonButton } from './ui/NeonButton';
import { NeonTextField } from './ui/NeonTextField';
import { NetworkStatusBar, NoInternetBanner } from './ui/NetworkStatusUi';
import { ScreenContent } from './ui/ScreenScaffold';
import { useLanguageSheet } from './ui/useLanguageSheet';

/**
 * Why the operator is back at the gate.
 *
 * `null` is a cold start — the device was never provisioned. The other two are
 * a key that existed and was taken away, and they are worth telling apart: one
 * is something the backend did to the device, the other is something the person
 * did on purpose, and an empty field with no explanation reads as a crash.
 *
 * Only `'revoked'` draws anything. A removal is something the person standing
 * in front of the phone did thirty seconds ago in Settings, and there is no
 * ported string for it — inventing one here would ship English text to the
 * three locales that have no translation of it, on the one screen a device is
 * most likely to be handed over already speaking the wrong language.
 */
export type GateReason = 'revoked' | 'removed';

/**
 * The key-entry gate — a port of `ui/keyentry/KeyEntryScreen.kt`.
 *
 * Layout is Android's, unchanged: the status/language bar in the flow (never an
 * overlay — as absolutely-positioned siblings they rendered on top of the app
 * title on a short screen, or on any screen with the keyboard up), then a
 * centred column that scrolls when it has to.
 *
 * ### The key never leaves `src/lib/api.ts`
 *
 * This screen used to build its own `fetch` with an `X-Device-Key` header
 * inline. That is the invariant this app shares with the Android one keeping
 * its two OkHttp clients apart, and it is not a style rule: the moment a header
 * is assembled at a call site, the next person to need "just one more request"
 * copies it, and eventually a key ends up on a request to a domain we are
 * merely probing. `createApiClient(key)` closes over the key and is the only
 * thing in the app that spells that header. The component still holds the typed
 * characters in state — it is a text field — but it never addresses anything
 * with them.
 *
 * ### What Android does after `/me` that this does not
 *
 * `KeyEntryViewModel.submit` verifies the key, **saves it to the key store**,
 * self-reports device info, adopts the server config and registers the
 * WorkManager heartbeat. None of those exist here. There is no key store (the
 * key lives in the icon's URL or in memory for a session, SPEC §13 — persisting
 * it is the one thing this app must never do), and no scheduler to register:
 * `useCheckLoop` arms its own timer when the shell mounts and re-reads config
 * at the head of every cycle, so there is no "adopt" step whose absence would
 * leave a freshly-keyed device sitting idle.
 */
export function KeyEntryScreen({
  reason = null,
  onKeySubmit,
}: {
  reason?: GateReason | null;
  onKeySubmit(key: string): void;
}): JSX.Element {
  const { t } = useI18n();
  // The gate reads connectivity itself rather than being handed it. It lives
  // outside `MainShell`, which is where the single `useNetworkStatus` for the
  // three tabs is hoisted, and the shell does not exist yet at this point.
  const status = useNetworkStatus();

  const [key, setKey] = useState('');
  const [loading, setLoading] = useState(false);
  /**
   * The error is stored as a **string key**, not as a rendered sentence.
   *
   * Android carries `UiText.Res` through its UiState for the same reason: the
   * language picker is on this very screen, and a message resolved once at the
   * moment it failed would stay in the old language after the operator switched
   * to the one they can read — which is precisely the person who is going to
   * switch.
   */
  const [error, setError] = useState<StringKey | null>(null);
  // The gate owns its own picker — see `ui/useLanguageSheet.tsx` for why there
  // are two instances and only one copy of the code around them.
  const language = useLanguageSheet();

  async function submit(): Promise<void> {
    const trimmed = key.trim();
    // Checked before any network call, exactly as the Kotlin does: a blank
    // field is our own answer, and firing a request to have the backend say
    // `401` to an empty header would spend a round trip to reach a worse
    // message ("invalid key" for a key nobody typed).
    if (trimmed === '') {
      setError('err_key_required');
      return;
    }
    // Android's `if (_state.value.loading) return`. The button is disabled
    // while a verification is in flight and the field with it, but the Enter
    // key can still arrive from a hardware keyboard, and a second request would
    // race the first for the same `onKeySubmit`.
    if (loading) return;

    setLoading(true);
    setError(null);
    try {
      await createApiClient(trimmed).me();
      // Deliberately no `setLoading(false)` on the way out. The parent swaps
      // this screen for the shell on the very next render, so clearing it is at
      // best a state update on a component that is gone; leaving the spinner up
      // also means the button cannot be pressed again during the frame between
      // the response and the unmount.
      onKeySubmit(trimmed);
    } catch (failure) {
      setLoading(false);
      /*
       * Two outcomes where Android has three. Its third branch is
       * `UiText.ofMessage(e.message, fallback = err_unknown)` — worth having
       * there, because a Retrofit/OkHttp exception message names a real cause.
       * A browser's does not: `fetch` rejects with "Load failed" on Safari for
       * a DNS failure, a CORS preflight the backend never answered, a timeout
       * and an offline radio alike. Putting that on screen would be an
       * untranslated English fragment that tells an operator nothing, so
       * everything short of a `401` collapses into the one honest instruction —
       * the server could not be reached.
       */
      const invalid = failure instanceof ApiError && failure.status === UNAUTHORIZED;
      setError(invalid ? 'err_key_invalid' : 'err_server_unreachable');
    }
  }

  return (
    <AppBackground>
      <div className="screen">
        <GateTopBar status={status} onOpenLanguage={language.open} />

        <ScreenContent className="screen-content--centred">
          <div className="gate__column">
            {/* Renders null when online — see NoInternetBanner. The wrapper is
                what carries the gate's spacing, and `.gate__banner:empty`
                collapses it so an online device does not pay 16px for a banner
                that is not there. */}
            <div className="gate__banner">
              <NoInternetBanner status={status} />
            </div>

            <GlobeHero className="gate__hero" />

            <h1 className="gate__title t-headline-medium">{t('app_name')}</h1>
            <p className="gate__subtitle t-body-medium">{t('keyentry_subtitle')}</p>

            {/*
             * Why the device was kicked back here, above the field it has to be
             * fixed in. Android has no counterpart: it wipes its key store on a
             * `401` and returns to the gate with nothing to say, because the
             * only fix is the same one the field offers. Here the fix is
             * usually *not* the field — a WebClip is provisioned by its icon's
             * URL — so the hint underneath is the actionable half.
             *
             * It stays put while the operator tries a key. The field's own
             * error says what happened to *this attempt*; the banner says why
             * the session ended, and those are different facts.
             */}
            {reason === 'revoked' ? (
              <>
                {/* The same banner the offline warning above draws — see
                    `ui/AlertBanner.tsx`. `gate-error` adds nothing but the
                    bottom margin that separates it from the field. */}
                <AlertBanner icon="error-outline" className="gate-error">
                  <div className="gate-error__text">
                    <p className="t-title-small">{t('ios_key_revoked_title')}</p>
                    <p className="t-body-medium">{t('ios_key_revoked_body')}</p>
                  </div>
                </AlertBanner>
                {/* Outside the banner on purpose: the advice stays true whether
                    or not the key was rejected, and it is the half an operator
                    acts on — so it reads as instruction rather than more red. */}
                <p className="gate-error__hint caption t-body-small">{t('ios_key_revoked_hint')}</p>
              </>
            ) : null}

            {/* The field has no `className` of its own, so the width comes from
                the wrapper — inside a centred flex column a bare block child
                shrinks to its content. */}
            <div className="gate__field">
              <NeonTextField
                value={key}
                onChange={setKey}
                label={t('keyentry_field_label')}
                error={error === null ? null : t(error)}
                disabled={loading}
                // Android sets `ImeAction.Done` and lets it dismiss the
                // keyboard, because its button is never covered. On an iPhone
                // the software keyboard takes half the viewport and the submit
                // button is below it, so Done submits rather than making the
                // operator dismiss the keyboard to reach a button they can then
                // no longer see the field above.
                onSubmit={() => {
                  void submit();
                }}
              />
            </div>

            <NeonButton
              text={t('keyentry_submit')}
              onClick={() => {
                void submit();
              }}
              loading={loading}
              className="gate__submit"
            />

            <DeviceInfoCard className="gate__device" />
          </div>
        </ScreenContent>
      </div>

      {language.sheet}
    </AppBackground>
  );
}

/**
 * Network state on the left, language on the right — Android's private
 * `GateTopBar`.
 *
 * **The language control belongs on this screen specifically.** It is the one
 * place a device can be handed over already showing a language the operator
 * cannot read, and it is the only screen the tab bar — which is where the other
 * way into the picker lives — cannot reach.
 *
 * No carrier is passed to the bar, though it takes one and Android supplies
 * `ipInfo?.isp`. Nothing has looked the device up yet: on this side the only
 * thing that calls ipwho.is is a check cycle, and a cycle cannot start without
 * the key this screen exists to collect. Firing that lookup here would mean a
 * request to a third party from a device that has not been provisioned, to
 * decorate a bar with a fact that has no bearing on whether the key is good.
 */
function GateTopBar({
  status,
  onOpenLanguage,
}: {
  status: NetStatus;
  onOpenLanguage(): void;
}): JSX.Element {
  const { t, locale } = useI18n();
  return (
    <div className="gate-top-bar">
      <NetworkStatusBar status={status} className="gate-top-bar__status" />
      <button type="button" className="text-btn" onClick={onOpenLanguage}>
        {/*
         * The description sits on the icon rather than as an `aria-label` on the
         * button, which is Android's structure too (`contentDescription` on the
         * `Icon`, the short code as a sibling `Text`). It matters here: an
         * `aria-label` would *replace* the accessible name, so the button would
         * announce "Change language" and swallow which language is currently
         * on. Named this way it reads "Change language, EN".
         */}
        <Icon name="language" size={18} title={t('a11y_change_language')} />
        {/* Always in its own script, so it is recognisable whatever language
            the rest of the screen happens to be in. */}
        <span className="t-label-large">{locale.shortCode}</span>
      </button>
    </div>
  );
}
